import {
  FullLoanDeal,
  LoanDeal,
  Property,
  Ticket,
  LoanLedgerEntry,
  Document,
  ClientCommitment,
  Borrower,
  FinancialTerms,
  LoanEvent,
  Notification,
  ClientLead,
  LoanDealVersion,
  BorrowerLedgerEntry,
  RedemptionStatement,
  BorrowerLoanApplication,
} from '../models/loan.model';
import { collection, onSnapshot, query, where, orderBy, limit } from 'firebase/firestore'; // FIX: Consolidated imports
import { FIRESTORE } from '../firebase.config';
import { sanitizeData } from '../utils/firebase.utils';
import { UserProfile } from '../models/user.model';
import { Injectable, inject, signal, computed, effect } from '@angular/core';
// FIX: Imported AuthService to resolve injection type errors.
import { AuthService } from '../auth/auth.service';
import { AppCacheService } from './app-cache.service';

function mapBy<T, K>(items: T[], keyFn: (item: T) => K): Map<K, T> {
  const result = new Map<K, T>();
  for (const item of items) {
    result.set(keyFn(item), item);
  }
  return result;
}

function groupBy<T, K>(items: T[], keyFn: (item: T) => K): Map<K, T[]> {
  const result = new Map<K, T[]>();
  for (const item of items) {
    const key = keyFn(item);
    const bucket = result.get(key);
    if (bucket) {
      bucket.push(item);
    } else {
      result.set(key, [item]);
    }
  }
  return result;
}

export function looksLikeCommitmentPlaceholder(name: string | undefined, userId: string, clientId?: string): boolean {
  if (!name) {
    return true;
  }

  const normalized = name.trim();
  return normalized === userId
    || normalized === clientId
    || /^CLIENT-[A-Z0-9]+$/i.test(normalized);
}

export function normalizeCommitmentRecord(
  commitment: ClientCommitment,
  usersById: Map<string, string>
): ClientCommitment {
  const raw = commitment as ClientCommitment & {
    clientId?: string;
    accountId?: string;
  };

  const userId = raw.userId || raw.clientId || '';
  const mappedName = usersById.get(userId)?.trim();
  const storedDisplayName = raw.clientDisplayName?.trim();
  const candidateName = raw.clientName?.trim();
  const fallbackName = storedDisplayName || mappedName || candidateName || userId || 'Unknown Client';
  const clientName = looksLikeCommitmentPlaceholder(candidateName, userId, raw.clientId)
    ? fallbackName
    : candidateName;

  return {
    ...commitment,
    userId,
    clientName,
    clientDisplayName: storedDisplayName || mappedName || undefined,
    clientAccountId: raw.clientAccountId || raw.accountId || '',
  };
}

export type ClientCommitmentSubscriptionScope = 'all' | 'confirmed' | 'none';

export function getClientCommitmentSubscriptionScope(
  userRole?: string | null,
  subRole?: UserProfile['subRole']
): ClientCommitmentSubscriptionScope {
  if (!userRole || userRole.toLowerCase() !== 'client') {
    return 'none';
  }

  if (subRole === 'Trustee' || subRole === 'ReadOnly') {
    return 'all';
  }

  return 'confirmed';
}

@Injectable({
  providedIn: 'root',
})
export class DataService {
  private authService = inject(AuthService);
  private cache = inject(AppCacheService);
  private unsubscribers: (() => void)[] = [];
  private userScopedUnsubscribers: (() => void)[] = [];
  private readonly _dataRevision = signal(0);
  private readonly actingContextListener = () => this.refreshUserScopedSubscriptions();

  // Firestore collection data signals - now public readonly
  readonly users = signal<UserProfile[]>([]);
  readonly loanDeals = signal<LoanDeal[]>([]);
  readonly properties = signal<Property[]>([]);
  readonly borrowers = signal<Borrower[]>([]);
  readonly financialTerms = signal<FinancialTerms[]>([]);
  readonly documents = signal<Document[]>([]);
  // private _clientCommitments holds the admin-wide commitment feed
  private readonly _clientCommitments = signal<ClientCommitment[]>([]);
  // private _currentUserCommitments holds the active client-context commitments
  private readonly _currentUserCommitments = signal<ClientCommitment[]>([]);

  // Public readonly computed signal that merges both commitment sources
  readonly clientCommitments = computed(() => {
    const all = [...this._clientCommitments(), ...this._currentUserCommitments()];
    const usersById = new Map(this.users().map(user => [user.uid, user.displayName]));
    const unique = new Map<string, ClientCommitment>();
    // The active client-context commitments from the subcollection are the source of truth.
    all.forEach(commitment => {
      const normalized = this.normalizeCommitment(commitment, usersById);
      unique.set(normalized.id, normalized);
    });
    return Array.from(unique.values());
  });

  readonly loanEvents = signal<LoanEvent[]>([]);
  readonly loanLedger = signal<LoanLedgerEntry[]>([]); // Client-facing capital ledger
  readonly borrowerLedger = signal<BorrowerLedgerEntry[]>([]); // NEW: Borrower-facing transaction ledger
  readonly borrowerApplications = signal<BorrowerLoanApplication[]>([]);
  readonly tickets = signal<Ticket[]>([]);
  readonly notifications = signal<Notification[]>([]);
  readonly clientLeads = signal<ClientLead[]>([]);
  readonly loanDealVersions = signal<LoanDealVersion[]>([]);
  readonly redemptionStatements = signal<RedemptionStatement[]>([]);
  readonly dataRevision = computed(() => this._dataRevision());

  readonly usersById = computed(() => mapBy(this.users(), user => user.uid));
  readonly loanDealsById = computed(() => mapBy(this.loanDeals(), deal => deal.id));
  readonly borrowersByDealId = computed(() => mapBy(this.borrowers(), borrower => borrower.loanDealId));
  readonly financialTermsByDealId = computed(() => mapBy(this.financialTerms(), term => term.loanDealId));
  readonly propertiesByDealId = computed(() => groupBy(this.properties(), property => property.loanDealId));
  readonly commitmentsByDealId = computed(() => groupBy(this.clientCommitments(), commitment => commitment.loanDealId));
  readonly commitmentsByUserId = computed(() => groupBy(this.clientCommitments(), commitment => commitment.userId));
  readonly loanLedgerByDealId = computed(() => groupBy(this.loanLedger(), entry => entry.loanDealId));
  readonly borrowerLedgerByDealId = computed(() => groupBy(this.borrowerLedger(), entry => entry.loanDealId));
  readonly borrowerApplicationsByBorrowerUserId = computed(() => groupBy(this.borrowerApplications(), application => application.borrowerUserId));
  readonly documentsByDealId = computed(() => groupBy(this.documents(), document => document.loanDealId));
  readonly ticketsByUserId = computed(() => groupBy(this.tickets(), ticket => ticket.userId));
  readonly notificationsByUserId = computed(() => groupBy(this.notifications(), notification => notification.userId));

  // Intermediate computed signal to join documents to their properties
  private propertiesWithDocuments = computed(() => {
    const props = this.properties();
    const docsByPropertyId = groupBy(this.documents(), doc => doc.propertyId || '');
    return props.map(property => ({
      ...property,
      documents: docsByPropertyId.get(property.id) ?? []
    }));
  });

  // Intermediate computed signal to join most related data to deals
  private dealsWithRelatedData = computed(() => {
    const deals = this.loanDeals();
    const borrowersByDealId = this.borrowersByDealId();
    const termsByDealId = this.financialTermsByDealId();
    const eventsByDealId = groupBy(this.loanEvents(), event => event.loanDealId);
    const loanLedgersByDealId = this.loanLedgerByDealId();
    const borrowerLedgersByDealId = this.borrowerLedgerByDealId();

    return deals.map(deal => {
      const borrower = borrowersByDealId.get(deal.id);
      const ft = termsByDealId.get(deal.id);

      if (!borrower) {
        console.warn(`DataService: Missing borrower for deal ${deal.id} (${deal.loanDealName})`);
      }
      if (!ft) {
        console.warn(`DataService: Missing financialTerms for deal ${deal.id} (${deal.loanDealName})`);
      }

      return {
        loanDeal: deal,
        borrower: borrower!, // Still using ! as it's required in FullLoanDeal interface, but we've logged it
        financialTerms: ft!,
        commitments: this.commitmentsByDealId().get(deal.id) ?? [],
        events: eventsByDealId.get(deal.id) ?? [],
        loanLedger: loanLedgersByDealId.get(deal.id) ?? [],
        borrowerLedger: borrowerLedgersByDealId.get(deal.id) ?? [],
      };
    });
  });

  // Final client-side join to construct the FullLoanDeal object
  allDeals = computed<FullLoanDeal[]>(() => {
    const dealsWithData = this.dealsWithRelatedData();
    const propsWithDocs = this.propertiesWithDocuments();
    const allDocs = this.documents();

    return dealsWithData.map(dealWithData => {
      const dealId = dealWithData.loanDeal.id;
      return {
        ...dealWithData,
        properties: propsWithDocs.filter(p => p.loanDealId === dealId),
        documents: allDocs.filter(d => d.loanDealId === dealId && !d.propertyId)
      };
    });
  });

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('acting-context-changed', this.actingContextListener);
    }

    effect(() => {
      this.cleanupSubscriptions();
      if (this.authService.isAuthenticated()) {
        this.initializeSubscriptions();
      } else {
        // Clear data on logout
        this.users.set([]);
        this.loanDeals.set([]);
        this.properties.set([]);
        this.borrowers.set([]);
        this.financialTerms.set([]);
        this.documents.set([]);
        this._clientCommitments.set([]);
        this._currentUserCommitments.set([]);
        this.loanEvents.set([]);
        this.loanLedger.set([]);
        this.borrowerLedger.set([]); // NEW: Clear on logout
        this.borrowerApplications.set([]);
        this.tickets.set([]);
        this.notifications.set([]);
        this.clientLeads.set([]);
        this.loanDealVersions.set([]);
        this.redemptionStatements.set([]);
        this.cache.clear();
        this.bumpRevision();
      }
    });
  }

  private initializeSubscriptions(): void {
    const userRole = this.authService.currentUserRole();
    const user = this.authService.userProfile();
    const commitmentScope = getClientCommitmentSubscriptionScope(userRole, user?.subRole);

    // Public collections are subscribed to by everyone
    this.createSubscription<LoanDeal>(collection(FIRESTORE, 'loanDeals'), data => this.loanDeals.set(data), 'loanDeals');
    this.createSubscription<Property>(collection(FIRESTORE, 'properties'), data => this.properties.set(data), 'properties');
    this.createSubscription<Borrower>(collection(FIRESTORE, 'borrowers'), data => this.borrowers.set(data), 'borrowers');
    this.createSubscription<FinancialTerms>(collection(FIRESTORE, 'financialTerms'), data => this.financialTerms.set(data), 'financialTerms');
    this.createSubscription<Document>(collection(FIRESTORE, 'documents'), data => this.documents.set(data), 'documents');

    // Role-dependent collections
    if (userRole?.toLowerCase() === 'admin') {
      // Admin gets everything
      this.createSubscription<UserProfile>(collection(FIRESTORE, 'users'), data => this.users.set(data), 'users');
      this.createSubscription<ClientCommitment>(collection(FIRESTORE, 'clientCommitments'), data => this._clientCommitments.set(data), 'clientCommitments');
      this.createSubscription<BorrowerLoanApplication>(collection(FIRESTORE, 'borrowerApplications'), data => this.borrowerApplications.set(data), 'borrowerApplications');
      this._currentUserCommitments.set([]);
      this.createSubscription<Ticket>(collection(FIRESTORE, 'tickets'), data => this.tickets.set(data), 'tickets');
      this.createSubscription<LoanEvent>(collection(FIRESTORE, 'loanEvents'), data => this.loanEvents.set(data), 'loanEvents');
      this.createSubscription<LoanLedgerEntry>(collection(FIRESTORE, 'loanLedger'), data => {
        console.log('DataService: Received loanLedger from Firestore:', JSON.stringify(data));
        this.loanLedger.set(data);
      }, 'loanLedger');
      this.createSubscription<BorrowerLedgerEntry>(collection(FIRESTORE, 'borrowerLedger'), data => this.borrowerLedger.set(data), 'borrowerLedger'); // NEW: Admin subscribes to borrower ledger
      this.createSubscription<ClientLead>(collection(FIRESTORE, 'clientLeads'), data => this.clientLeads.set(data), 'clientLeads');
      this.createSubscription<LoanDealVersion>(collection(FIRESTORE, 'loanDealVersions'), data => this.loanDealVersions.set(data), 'loanDealVersions');
      this.createSubscription<Notification>(collection(FIRESTORE, 'notifications'), data => this.notifications.set(data), 'notifications');
      this.createSubscription<RedemptionStatement>(collection(FIRESTORE, 'redemptionStatements'), data => this.redemptionStatements.set(data), 'redemptionStatements');

    } else if (user && userRole === 'Client') {
      if (commitmentScope === 'all') {
        this.createSubscription<ClientCommitment>(
          collection(FIRESTORE, 'clientCommitments'),
          data => this._clientCommitments.set(data),
          'clientCommitments'
        );
      } else if (commitmentScope === 'confirmed') {
        this.createSubscription<ClientCommitment>(
          query(collection(FIRESTORE, 'clientCommitments'), where('isConfirmed', '==', true)),
          data => this._clientCommitments.set(data),
          'confirmedClientCommitments'
        );
      } else {
        this._clientCommitments.set([]);
      }

      this._currentUserCommitments.set([]);
      this.initializeClientUserScopedSubscriptions(user);

      // Explicitly clear admin-only data for clients to prevent stale data.
      this.users.set([]);
      this.loanEvents.set([]);
      this.loanLedger.set([]);
      this.borrowerLedger.set([]); // NEW: Clear on logout/for clients
      this.clientLeads.set([]);
      this.loanDealVersions.set([]);

    } else if (user && userRole === 'Borrower') {
      this._clientCommitments.set([]);
      this._currentUserCommitments.set([]);
      this.initializeBorrowerUserScopedSubscriptions(user);

      // Borrowers should only access their own user-scoped tickets/notifications.
      this.users.set([]);
      this.loanEvents.set([]);
      this.loanLedger.set([]);
      this.borrowerLedger.set([]);
      this.clientLeads.set([]);
      this.loanDealVersions.set([]);
      this.redemptionStatements.set([]);

    } else {
      // User has no role or an unknown role, clear all protected data.
      this.users.set([]);
      this._clientCommitments.set([]);
      this._currentUserCommitments.set([]);
      this.tickets.set([]);
      this.loanEvents.set([]);
      this.loanLedger.set([]);
      this.borrowerLedger.set([]); // NEW: Clear on logout/for unknown role
      this.notifications.set([]);
      this.clientLeads.set([]);
      this.loanDealVersions.set([]);
      this.redemptionStatements.set([]);
    }
  }

  private cleanupSubscriptions(): void {
    this.unsubscribers.forEach(unsub => unsub());
    this.unsubscribers = [];
    this.cleanupUserScopedSubscriptions();
  }

  private cleanupUserScopedSubscriptions(): void {
    this.userScopedUnsubscribers.forEach(unsub => unsub());
    this.userScopedUnsubscribers = [];
  }

  private createSubscription<T>(
    q: any,
    signalUpdater: (data: T[]) => void,
    name: string,
    target: (() => void)[] = this.unsubscribers
  ): void {
    const unsubscribe = onSnapshot(q, snapshot => {
      const data = snapshot.docs.map(doc => sanitizeData({ ...doc.data(), id: doc.id })) as T[];
      signalUpdater(data);
      this.bumpRevision();
    }, error => console.error(`Error fetching ${name}: `, error));
    target.push(unsubscribe);
  }

  private refreshUserScopedSubscriptions(): void {
    const userRole = this.authService.currentUserRole();
    const user = this.authService.userProfile();
    if (!user || (userRole !== 'Client' && userRole !== 'Borrower')) {
      return;
    }

    this.cleanupUserScopedSubscriptions();
    if (userRole === 'Client') {
      this.initializeClientUserScopedSubscriptions(user);
      return;
    }

    this.initializeBorrowerUserScopedSubscriptions(user);
  }

  private initializeClientUserScopedSubscriptions(user: UserProfile): void {
    this.cleanupUserScopedSubscriptions();
    const effectiveUserId = this.getEffectiveUserIdForSubscriptions(user);
    const userDocPath = `users/${effectiveUserId}`;
    const userCommitmentsQuery = collection(FIRESTORE, `${userDocPath}/clientCommitments`);

    this.createSubscription<ClientCommitment>(
      userCommitmentsQuery,
      data => this._currentUserCommitments.set(data),
      'currentUserCommitments',
      this.userScopedUnsubscribers
    );

    const ticketsQuery = query(collection(FIRESTORE, `${userDocPath}/tickets`), limit(100));
    const notificationsQuery = query(collection(FIRESTORE, `${userDocPath}/notifications`), orderBy('timestamp', 'desc'), limit(100));
    const statementsQuery = query(collection(FIRESTORE, 'redemptionStatements'), where('userId', '==', effectiveUserId));

    this.createSubscription<Ticket>(ticketsQuery, data => this.tickets.set(data), 'tickets', this.userScopedUnsubscribers);
    this.createSubscription<Notification>(notificationsQuery, data => this.notifications.set(data), 'notifications', this.userScopedUnsubscribers);
    this.createSubscription<RedemptionStatement>(statementsQuery, data => this.redemptionStatements.set(data), 'redemptionStatements', this.userScopedUnsubscribers);
  }

  private initializeBorrowerUserScopedSubscriptions(user: UserProfile): void {
    this.cleanupUserScopedSubscriptions();
    const userDocPath = `users/${user.uid}`;
    const ticketsQuery = query(collection(FIRESTORE, `${userDocPath}/tickets`), limit(100));
    const notificationsQuery = query(collection(FIRESTORE, `${userDocPath}/notifications`), orderBy('timestamp', 'desc'), limit(100));
    const applicationsQuery = query(collection(FIRESTORE, 'borrowerApplications'), where('borrowerUserId', '==', user.uid));

    this._currentUserCommitments.set([]);
    this.redemptionStatements.set([]);
    this.createSubscription<Ticket>(ticketsQuery, data => this.tickets.set(data), 'borrowerTickets', this.userScopedUnsubscribers);
    this.createSubscription<Notification>(notificationsQuery, data => this.notifications.set(data), 'borrowerNotifications', this.userScopedUnsubscribers);
    this.createSubscription<BorrowerLoanApplication>(applicationsQuery, data => this.borrowerApplications.set(data), 'borrowerApplications', this.userScopedUnsubscribers);
  }

  private getEffectiveUserIdForSubscriptions(user: UserProfile): string {
    if (typeof window === 'undefined') {
      return user.uid;
    }

    if (user.subRole === 'Trustee' || user.subRole === 'ReadOnly') {
      const actingForClientId = sessionStorage.getItem('actingForClientId');
      if (actingForClientId) {
        return actingForClientId;
      }
    }

    return user.uid;
  }

  private bumpRevision(): void {
    this._dataRevision.update(revision => revision + 1);
  }

  /**
   * Normalizes migrated/legacy commitment records so UI flows remain stable even when
   * backend payload field names or values drift (e.g., clientName saved as userId).
   */
  private normalizeCommitment(
    commitment: ClientCommitment,
    usersById: Map<string, string>
  ): ClientCommitment {
    return normalizeCommitmentRecord(commitment, usersById);
  }

  /**
   * Utility method to query a Firestore collection with filters
   */
  async query<T>(
    collectionName: string,
    filters: Array<{ field: string; operator: any; value: any }>,
    limitCount?: number,
    options?: { bypassCache?: boolean }
  ): Promise<T[]> {
    try {
      const fetcher = async () => {
        let q = collection(FIRESTORE, collectionName) as any;
        const constraints: any[] = [];

        if (filters.length > 0) {
          constraints.push(...filters.map(f => where(f.field, f.operator, f.value)));
        }

        if (limitCount) {
          const { limit } = await import('firebase/firestore');
          constraints.push(limit(limitCount));
        }

        if (constraints.length > 0) {
          q = query(q, ...constraints);
        }

        const snapshot = await import('firebase/firestore').then(m => m.getDocs(q));
        return snapshot.docs.map(doc => sanitizeData({ ...(doc.data() as any), id: doc.id }) as T);
      };

      if (options?.bypassCache) {
        return await fetcher();
      }

      const cacheKey = [
        'firestore-query',
        this.dataRevision(),
        collectionName,
        JSON.stringify(filters),
        limitCount ?? 'no-limit',
      ].join(':');

      return await this.cache.readThrough(cacheKey, fetcher, 30_000);
    } catch (error) {
      console.error(`Error querying ${collectionName}:`, error);
      return [];
    }
  }

  /**
   * Utility method to add a document to a Firestore collection
   */
  async add(collectionName: string, data: any): Promise<string> {
    try {
      const { addDoc, collection: firestoreCollection } = await import('firebase/firestore');
      const ref = await addDoc(firestoreCollection(FIRESTORE, collectionName), data);
      this.bumpRevision();
      return ref.id;
    } catch (error) {
      console.error(`Error adding document to ${collectionName}:`, error);
      throw error;
    }
  }

  /**
   * Utility method to update a Firestore document
   */
  async updateDocument(path: string, data: any): Promise<void> {
    try {
      const { doc, updateDoc } = await import('firebase/firestore');
      const docRef = doc(FIRESTORE, path);
      await updateDoc(docRef, data);
      this.bumpRevision();
    } catch (error) {
      console.error(`Error updating document at ${path}:`, error);
      throw error;
    }
  }

  /**
   * Manually remove a commitment from the local cache.
   * This is used when a user (e.g. Trustee) deletes a commitment but cannot delete the top-level document.
   * We remove it locally so the UI updates immediately.
   */
  removeLocalCommitment(commitmentId: string): void {
    const filterOutCommitment = (commitments: ClientCommitment[]) => commitments.filter(c => c.id !== commitmentId);
    this._clientCommitments.set(filterOutCommitment(this._clientCommitments()));
    this._currentUserCommitments.set(filterOutCommitment(this._currentUserCommitments()));
    this.bumpRevision();
  }

  /**
   * Manually update a commitment in the local cache.
   * This keeps optimistic/local state aligned while Firestore listeners catch up.
   */
  updateLocalCommitment(commitmentId: string, updates: Partial<ClientCommitment>): void {
    const applyUpdates = (commitments: ClientCommitment[]) => commitments.map(c => {
      if (c.id === commitmentId) {
        return { ...c, ...updates };
      }
      return c;
    });

    this._clientCommitments.set(applyUpdates(this._clientCommitments()));
    this._currentUserCommitments.set(applyUpdates(this._currentUserCommitments()));
    this.bumpRevision();
  }

  upsertLocalLoanLedgerEntry(entry: LoanLedgerEntry): void {
    const current = this.loanLedger();
    const index = current.findIndex(candidate => candidate.id === entry.id);

    if (index >= 0) {
      this.loanLedger.set(current.map(candidate => candidate.id === entry.id ? { ...candidate, ...entry } : candidate));
    } else {
      this.loanLedger.set([...current, entry]);
    }

    this.bumpRevision();
  }

  removeLocalLoanLedgerEntriesBySource(sourceId: string, entryType?: LoanLedgerEntry['entryType']): void {
    const current = this.loanLedger();
    const next = current.filter(entry => {
      if (entry.sourceId !== sourceId) {
        return true;
      }

      if (entryType && entry.entryType !== entryType) {
        return true;
      }

      return false;
    });

    if (next.length === current.length) {
      return;
    }

    this.loanLedger.set(next);
    this.bumpRevision();
  }

  getUserById(userId: string): UserProfile | null {
    return this.usersById().get(userId) ?? null;
  }

  getLoanDealById(dealId: string): LoanDeal | null {
    return this.loanDealsById().get(dealId) ?? null;
  }

  getBorrowerByDealId(dealId: string): Borrower | null {
    return this.borrowersByDealId().get(dealId) ?? null;
  }

  getFinancialTermsByDealId(dealId: string): FinancialTerms | null {
    return this.financialTermsByDealId().get(dealId) ?? null;
  }

  getPropertiesForDeal(dealId: string): Property[] {
    return this.propertiesByDealId().get(dealId) ?? [];
  }

  getCommitmentsForDeal(dealId: string): ClientCommitment[] {
    return this.commitmentsByDealId().get(dealId) ?? [];
  }

  getCommitmentsForUser(userId: string): ClientCommitment[] {
    return this.commitmentsByUserId().get(userId) ?? [];
  }

  getLoanLedgerForDeal(dealId: string): LoanLedgerEntry[] {
    return this.loanLedgerByDealId().get(dealId) ?? [];
  }

  getBorrowerLedgerForDeal(dealId: string): BorrowerLedgerEntry[] {
    return this.borrowerLedgerByDealId().get(dealId) ?? [];
  }

  getTicketsForUser(userId: string): Ticket[] {
    return this.ticketsByUserId().get(userId) ?? [];
  }

  getNotificationsForUser(userId: string): Notification[] {
    return this.notificationsByUserId().get(userId) ?? [];
  }
}
